import { Trans } from '@lingui/react/macro';
import { WheelSetPickupSigningOrderRecordCustomer } from '@navio/nsdk';
import { cn } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiButton, NoddiDummyButton, NoddiLink } from 'noddi-ui-web';
import { getLongAddressName } from 'noddi-web';
import { TireHotelSvg } from '../TireHotelSvg';

interface WheelPickupSigningCardProps {
  wheelSetPickups?: WheelSetPickupSigningOrderRecordCustomer[] | null;
  variant?: 'card' | 'button';
  className?: string;
}

export const WheelPickupSigningCard = ({
  wheelSetPickups,
  variant = 'card',
  className = ''
}: WheelPickupSigningCardProps) => {
  // Handle wheel pickup signing logic
  const wheelPickupSigningOrders = wheelSetPickups || [];

  const lastDocument = wheelPickupSigningOrders
    .sort((a, b) => new Date(b.signingUrlExpiresAt).getTime() - new Date(a.signingUrlExpiresAt).getTime())
    .find((order) => order);

  const hasSigned = !!lastDocument?.documentsSigned.length;
  const signingUrl = lastDocument?.signingUrl;
  const showPoaContent = !hasSigned && signingUrl && new Date(lastDocument?.signingUrlExpiresAt) > new Date();

  // If there are multiple documents, show count
  const totalDocuments = wheelPickupSigningOrders.length;
  const signedDocuments = wheelPickupSigningOrders.filter((doc) => doc.documentsSigned.length > 0).length;

  // Don't render anything if there's nothing to show
  if (!showPoaContent && !hasSigned) {
    return null;
  }

  // Button variant for status updates
  if (variant === 'button') {
    if (showPoaContent) {
      return (
        <NoddiLink to={signingUrl} target='_blank' className={className}>
          <NoddiButton size='small' endIcon='ArrowRight'>
            <Trans>Sign power of attorney</Trans>
            {totalDocuments > 1 && ` (${signedDocuments}/${totalDocuments})`}
          </NoddiButton>
        </NoddiLink>
      );
    }

    if (hasSigned) {
      return (
        <div className={cn('flex flex-col gap-2', className)}>
          {lastDocument?.documentsSigned[0]?.pdfDocument.url && (
            <NoddiLink
              size='small'
              variant='outline'
              startIcon='Download'
              target='_blank'
              to={lastDocument.documentsSigned[0].pdfDocument.url}
            >
              <Trans>View signed power of attorney</Trans>
              {totalDocuments > 1 && ` (${signedDocuments}/${totalDocuments})`}
            </NoddiLink>
          )}
        </div>
      );
    }
  }

  // Card variant for confirmation page
  if (variant === 'card') {
    return (
      <div className={cn('flex flex-col gap-4', className)}>
        {/* Power of Attorney Card - show if signing is needed */}
        {showPoaContent && (
          <NoddiBasicCard openInNewTab className='flex flex-col gap-4' to={signingUrl}>
            <div className='flex justify-between gap-6'>
              <div className='flex flex-col gap-3'>
                <p>
                  <Trans>
                    To pick up your tires from another tire hotel, we need your signature on a power of attorney. It's
                    quick and helps us get everything ready for your booking.
                  </Trans>
                </p>

                {/* Show pickup location details */}
                {lastDocument && (
                  <div className='text-sm text-muted-foreground'>
                    <span>
                      <Trans>Pickup location:</Trans>
                    </span>
                    <div className='font-medium'>
                      {lastDocument.tireHotelLocation ? (
                        <span>{lastDocument.tireHotelLocation.name}</span>
                      ) : lastDocument.pickupAddress ? (
                        <span>{getLongAddressName(lastDocument.pickupAddress)}</span>
                      ) : (
                        <span className='text-gray-500'>
                          <Trans>Location not specified</Trans>
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {totalDocuments > 1 && (
                  <span className='text-sm text-muted-foreground'>
                    <Trans>
                      Documents signed: {signedDocuments}/{totalDocuments}
                    </Trans>
                  </span>
                )}
              </div>
            </div>
            <div className='flex items-end justify-between gap-5'>
              <div>
                <TireHotelSvg />
              </div>

              <NoddiDummyButton size='small' endIcon='ArrowRight'>
                <Trans>Sign power of attorney</Trans>
              </NoddiDummyButton>
            </div>
          </NoddiBasicCard>
        )}

        {/* Download link if documents are signed */}
        {hasSigned && lastDocument?.documentsSigned[0]?.pdfDocument.url && (
          <div className='flex flex-col gap-2'>
            <NoddiLink startIcon='Download' target='_blank' to={lastDocument.documentsSigned[0].pdfDocument.url}>
              <Trans>View signed power of attorney</Trans>
              {totalDocuments > 1 && ` (${signedDocuments}/${totalDocuments})`}
            </NoddiLink>
          </div>
        )}
      </div>
    );
  }

  return null;
};
